import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2 } from 'lucide-react';

const ContactForm = () => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        company: '',
        representative: '',
        email: '',
        phone: '',
        requirement: '',
        materialType: 'mixto',
        magnitude: '',
        specifications: ''
    });

    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus('loading');

        try {
            const scriptURL = 'https://script.google.com/macros/s/AKfycbwY6TNw83GsX1BQLgNNz2rbe02hFFFfOa5pYbleeKkGf_5KVKQFsNnfAnUMMGxNK20G/exec';

            await fetch(scriptURL, {
                method: 'POST',
                mode: 'no-cors',
                headers: {
                    'Content-Type': 'text/plain',
                },
                body: JSON.stringify(formData),
            });

            setStatus('success');
            setFormData({
                company: '',
                representative: '',
                email: '',
                phone: '',
                requirement: '',
                materialType: 'mixto',
                magnitude: '',
                specifications: ''
            });

            setTimeout(() => setStatus('idle'), 5000);

        } catch (error) {
            console.error('Error:', error);
            setStatus('error');
            setTimeout(() => setStatus('idle'), 4000);
        }
    };

    if (status === 'success') {
        return (
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center justify-center py-12 text-center"
            >
                <CheckCircle2 size={64} className="text-green-500 mb-6" />
                <h4 className="text-2xl font-heading font-bold text-gray-800 mb-2">{t('contactForm.msgSuccess')}</h4>
                <p className="text-gray-500 font-body max-w-sm mx-auto">
                    {t('contactForm.msgSuccessDesc')}
                </p>
                <button
                    onClick={() => setStatus('idle')}
                    className="mt-8 text-primary font-bold text-sm underline underline-offset-4"
                >
                    {t('contactForm.btnAnother')}
                </button>
            </motion.div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label className="block text-xs font-bold font-body text-gray-500 uppercase tracking-widest mb-2">{t('contactForm.company')}</label>
                    <input
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        type="text"
                        className="form-input"
                        placeholder={t('contactForm.companyPlaceholder')}
                        required
                    />
                </div>
                <div>
                    <label className="block text-xs font-bold font-body text-gray-500 uppercase tracking-widest mb-2">{t('contactForm.rep')}</label>
                    <input
                        name="representative"
                        value={formData.representative}
                        onChange={handleChange}
                        type="text"
                        className="form-input"
                        placeholder={t('contactForm.repPlaceholder')}
                        required
                    />
                </div>
                <div>
                    <label className="block text-xs font-bold font-body text-gray-500 uppercase tracking-widest mb-2">{t('contactForm.email')}</label>
                    <input
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        type="email"
                        className="form-input"
                        placeholder={t('contactForm.emailPlaceholder')}
                        required
                    />
                </div>
                <div>
                    <label className="block text-xs font-bold font-body text-gray-500 uppercase tracking-widest mb-2">{t('contactForm.phone')}</label>
                    <input
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        type="tel"
                        className="form-input"
                        placeholder="+1..."
                        required
                    />
                </div>
            </div>

            <div>
                <label className="block text-xs font-bold font-body text-gray-500 uppercase tracking-widest mb-2">{t('contactForm.req')}</label>
                <select
                    name="requirement"
                    value={formData.requirement}
                    onChange={handleChange}
                    className="form-select"
                    required
                >
                    <option value="" disabled>{t('contactForm.reqPlaceholder')}</option>
                    <option value="venta">{t('contactForm.reqSell')}</option>
                    <option value="compra">{t('contactForm.reqBuy')}</option>
                    <option value="desmantelamiento">{t('contactForm.reqClean')}</option>
                    <option value="transporte">{t('contactForm.reqTransport')}</option>
                </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label className="block text-xs font-bold font-body text-gray-500 uppercase tracking-widest mb-2">{t('contactForm.mat')}</label>
                    <select
                        name="materialType"
                        value={formData.materialType}
                        onChange={handleChange}
                        className="form-select"
                    >
                        <option value="ferroso">{t('contactForm.matFerrous')}</option>
                        <option value="no-ferroso">{t('contactForm.matNonFerrous')}</option>
                        <option value="mixto">{t('contactForm.matMixed')}</option>
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-bold font-body text-gray-500 uppercase tracking-widest mb-2">{t('contactForm.magnitude')}</label>
                    <input
                        name="magnitude"
                        value={formData.magnitude}
                        onChange={handleChange}
                        type="number"
                        className="form-input"
                        placeholder={t('contactForm.magnitudePlaceholder')}
                        min="1"
                    />
                </div>
            </div>

            <div>
                <label className="block text-xs font-bold font-body text-gray-500 uppercase tracking-widest mb-2">{t('contactForm.spec')}</label>
                <textarea
                    name="specifications"
                    value={formData.specifications}
                    onChange={handleChange}
                    className="form-input min-h-[120px] resize-y"
                    placeholder={t('contactForm.specPlaceholder')}
                    required
                ></textarea>
            </div>

            <div className="pt-4">
                <button
                    type="submit"
                    disabled={status === 'loading'}
                    className={`btn-primary w-full h-14 text-base shadow-md flex items-center justify-center gap-3 transition-all ${status === 'loading' ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                    {status === 'loading' ? (
                        <>
                            <Loader2 className="animate-spin" size={20} />
                            {t('contactForm.btnLoading')}
                        </>
                    ) : (
                        t('contactForm.btnSubmit')
                    )}
                </button>
                {status === 'error' && (
                    <p className="text-center text-xs text-red-500 mt-4">
                        {t('contactForm.msgError')}
                    </p>
                )}
                <p className="text-center text-xs text-gray-400 mt-4 max-w-md mx-auto">
                    {t('contactForm.msgDisclaimer')}
                </p>
            </div>
        </form>
    );
};

export default ContactForm;
